import React, { useCallback, useState } from 'react';
import { Lock, Shield, RefreshCw, Check, Copy, Trash, Key, AlertTriangle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import InputSection from './InputSection';
import HashSettingsSection from './HashSettingsSection';
import PasswordGeneratorSection from './PasswordGeneratorSection';
import ValidationSection from './ValidationSection';

/**
 * Компонент основной формы генерации Bcrypt-хешей
 */
const HashForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isGenerating, 
  setIsGenerating,
  result,
  setResult,
  validateResult,
  setValidateResult,
  saveHash
}) => {
  
  // Состояние для валидации хеша
  const [validationText, setValidationText] = useState('');
  const [validationHash, setValidationHash] = useState('');
  const [validationMode, setValidationMode] = useState(false);
  
  // Функция очистки полей ввода
  const clearFields = () => {
    setFormData(prev => ({
      ...prev,
      plaintext: '',
    }));
    setResult(null);
    setValidateResult(null);
    setValidationText('');
    setValidationHash('');
    setErrors({});
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Дополнительная логика если нужна
      if (name === 'rounds') {
        // Ограничиваем количество раундов в пределах 4-31
        const roundsNumber = parseInt(value, 10);
        if (!isNaN(roundsNumber)) {
          newState.rounds = Math.max(4, Math.min(31, roundsNumber));
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const { plaintext, rounds, passwordLength, autoGenerate } = formData;

    // Основные проверки
    if (!plaintext && !autoGenerate) {
      newErrors.plaintext = 'Введите текст для хеширования или включите автогенерацию пароля.';
    }

    // Проверка настроек раундов
    const roundsNumber = parseInt(rounds, 10);
    if (isNaN(roundsNumber) || roundsNumber < 4 || roundsNumber > 31) {
      newErrors.rounds = 'Количество раундов должно быть числом от 4 до 31.';
    }

    // Проверка настроек генератора паролей
    if (autoGenerate) {
      const lengthNum = parseInt(passwordLength, 10);
      if (isNaN(lengthNum) || lengthNum < 6 || lengthNum > 128) {
        newErrors.passwordLength = 'Длина пароля должна быть от 6 до 128 символов.';
      }

      // Проверяем, что хотя бы один набор символов выбран
      if (!formData.includeLowercase && !formData.includeUppercase && 
          !formData.includeNumbers && !formData.includeSymbols) {
        newErrors.characterSets = 'Выберите хотя бы один набор символов для генерации пароля.';
      }
    }

    // Для валидации хеша
    if (validationMode) {
      if (!validationText) {
        newErrors.validationText = 'Введите текст для проверки.';
      }
      if (!validationHash) {
        newErrors.validationHash = 'Введите хеш для проверки.';
      } else if (!validationHash.startsWith('$2')) {
        newErrors.validationHash = 'Введенная строка не похожа на Bcrypt-хеш. Хеш должен начинаться с "$2a$", "$2b$" или "$2y$".';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validationMode, validationText, validationHash, setErrors]);

  // Генерация случайного пароля
  const generateRandomPassword = () => {
    const { 
      passwordLength, 
      includeLowercase, 
      includeUppercase, 
      includeNumbers, 
      includeSymbols 
    } = formData;
    
    let charset = '';
    if (includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
    if (includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (includeNumbers) charset += '0123456789';
    if (includeSymbols) charset += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    // Если ничего не выбрано, используем буквы и цифры по умолчанию
    if (charset === '') charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    let password = '';
    const length = parseInt(passwordLength, 10) || 12;
    
    // Генерируем случайный пароль
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }

    return password;
  };

  // Генерация Bcrypt-хеша
  const generateBcryptHash = async () => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsGenerating(true);
    setResult(null);
    setValidateResult(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(async () => {
      try {
        // Подготавливаем входные данные
        let plaintextToHash = formData.plaintext;
        let isGenerated = false;

        // Если включена автогенерация, генерируем пароль
        if (formData.autoGenerate) {
          plaintextToHash = generateRandomPassword();
          isGenerated = true;
          
          // Обновляем форму с сгенерированным паролем
          setFormData(prev => ({
            ...prev,
            plaintext: plaintextToHash
          }));
        }

        // Формируем хеш (в реальном приложении тут был бы настоящий Bcrypt)
        // Имитация хеширования с задержкой
        const salt = generateSalt(formData.rounds);
        const hash = await fakeBcryptHash(plaintextToHash, salt, formData.rounds);

        // Создаем результат
        const hashResult = {
          plaintext: plaintextToHash,
          hash: hash,
          rounds: formData.rounds,
          format: formData.hashFormat,
          isGenerated: isGenerated,
          timestamp: new Date().toISOString()
        };

        // Обновляем состояние
        setResult(hashResult);
        
        // Сохраняем в историю если нужно
        if (formData.saveHistory) {
          saveHash(hashResult);
        }
      } catch (error) {
        console.error("Ошибка генерации хеша:", error);
        setErrors({ general: `Произошла ошибка при генерации хеша: ${error.message}.` });
      } finally {
        setIsGenerating(false);
      }
    }, 1000); // Симуляция нагрузки в течение 1 секунды
  };

  // Проверка хеша (валидация)
  const validateHash = async () => {
    if (!validationText || !validationHash) {
      setErrors({
        validationText: !validationText ? 'Введите текст для проверки' : undefined,
        validationHash: !validationHash ? 'Введите хеш для проверки' : undefined,
      });
      return;
    }

    setIsGenerating(true);
    setValidateResult(null);

    // Симуляция проверки с задержкой
    setTimeout(async () => {
      try {
        // Извлекаем параметры из хеша
        const hashParts = validationHash.split('$');
        let isValidFormat = hashParts.length >= 4 && validationHash.startsWith('$2');
        
        // Эмуляция проверки (в реальном приложении использовался бы bcrypt.compare)
        const isMatch = await fakeBcryptCompare(validationText, validationHash);

        setValidateResult({
          isMatch,
          isValidFormat,
          text: validationText,
          hash: validationHash,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error("Ошибка проверки хеша:", error);
        setErrors({ validationGeneral: `Произошла ошибка при проверке хеша: ${error.message}.` });
      } finally {
        setIsGenerating(false);
      }
    }, 1000); // Симуляция нагрузки
  };

  // Переключение режима валидации
  const toggleValidationMode = () => {
    setValidationMode(!validationMode);
    setValidateResult(null);
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validationMode) {
      validateHash();
    } else {
      generateBcryptHash();
    }
  };

  // Вспомогательные функции для имитации Bcrypt
  // В реальном приложении здесь был бы настоящий Bcrypt
  const generateSalt = (rounds) => {
    const version = ['$2a$', '$2b$', '$2y$'][Math.floor(Math.random() * 3)];
    const paddedRounds = rounds.toString().padStart(2, '0');
    const randomChars = Array(22).fill(0).map(() => {
      const charset = './ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      return charset.charAt(Math.floor(Math.random() * charset.length));
    }).join('');
    
    return `${version}${paddedRounds}$${randomChars}`;
  };

  const fakeBcryptHash = (text, salt, rounds) => {
    return new Promise((resolve) => {
      // Имитация хеша Bcrypt
      setTimeout(() => {
        const checksum = Array(31).fill(0).map(() => {
          const charset = './ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
          return charset.charAt(Math.floor(Math.random() * charset.length));
        }).join('');
        
        // Возвращаем соль + контрольную сумму
        resolve(salt + checksum);
      }, Math.min(rounds * 50, 500)); // Имитируем зависимость от количества раундов
    });
  };

  const fakeBcryptCompare = (text, hash) => {
    return new Promise((resolve) => {
      // Имитация проверки
      setTimeout(() => {
        // Детерминированная имитация сравнения для последовательных результатов
        // В реальной жизни это было бы cryptographically secure сравнение
        const textHash = text.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const hashSum = hash.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        
        // Имитация соответствия для демонстрации
        // В реальном приложении это было бы настоящее криптографическое сравнение
        resolve(text === 'password' && hash.startsWith('$2a$') || 
                text === 'test123' && hash.startsWith('$2b$') ||
                textHash % 17 === hashSum % 17); // Небольшая случайность
      }, 800);
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {validationMode ? (
              <>
                <Check className="w-6 h-6 text-green-500" />
                Проверка Bcrypt-хеша
              </>
            ) : (
              <>
                <Shield className="w-6 h-6 text-blue-500" />
                Генерация Bcrypt-хеша
              </>
            )}
          </h2>
          
          <Button
            type="button"
            onClick={toggleValidationMode}
            variant="outline"
            size="sm"
          >
            {validationMode ? 'Режим генерации' : 'Режим проверки'}
          </Button>
        </div>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}
        
        {/* Информация о производительности */}
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3 mt-1" />
            <div>
              <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                Важная информация о Bcrypt
              </h4>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">
                Bcrypt с большим количеством раундов намеренно потребляет значительные вычислительные ресурсы для защиты от перебора.
                При высоких значениях раундов (от 12) генерация может занимать от нескольких секунд, что является нормальным и обеспечивает защиту от атак методом перебора.
                Bcrypt с большим количеством раундов намеренно потребляет значительные вычислительные ресурсы для защиты от перебора.
                При высоких значениях раундов (от 12) генерация может занимать от нескольких секунд, что является нормальным и обеспечивает защиту от атак методом перебора.
              </p>
            </div>
          </div>
        </div>

        {validationMode ? (
          /* Секция валидации хеша */
          <ValidationSection
            validationText={validationText}
            setValidationText={setValidationText}
            validationHash={validationHash}
            setValidationHash={setValidationHash}
            errors={errors}
            validateResult={validateResult}
          />
        ) : (
          <>
            {/* Секция ввода текста для хеширования */}
            <InputSection
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
            />

            {/* Секция настроек алгоритма хеширования */}
            <HashSettingsSection
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
            />

            {/* Секция генератора паролей */}
            <PasswordGeneratorSection
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
            />
          </>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={validationMode ? <Check className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
            disabled={isGenerating || Object.keys(errors).length > 0}
            isLoading={isGenerating}
            size="lg"
          >
            {isGenerating 
              ? (validationMode ? 'Проверяем...' : 'Генерируем...') 
              : (validationMode ? 'Проверить хеш' : 'Сгенерировать хеш')}
          </Button>
          <Button
            type="button"
            onClick={clearFields}
            icon={<Trash className="w-5 h-5" />}
            variant="secondary"
            disabled={isGenerating}
            size="lg"
          >
            Очистить
          </Button>
          {!validationMode && (
            <Button
              type="button"
              onClick={() => {
                const password = generateRandomPassword();
                setFormData(prev => ({ ...prev, plaintext: password }));
              }}
              icon={<Key className="w-5 h-5" />}
              variant="secondary"
              disabled={isGenerating}
              size="lg"
            >
              Генерировать пароль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default HashForm;